import React, {ReactNode} from 'react';
import {Link, To, useNavigate} from 'react-router-dom';
import {ArrowLeftIcon} from 'lucide-react';
import Icon from '../Icon.tsx';
import MxNotifications from '../../notifications/MxNotifications.tsx';
import joinPath from '../../../lib/joinPath.ts';
import useClientConfig from '../../../hooks/useClientConfig.tsx';
import {useAuthStateManager} from '../../../hooks/useAuthStateManager.tsx';
import {Avatar} from '../../ds/CreatorIdentity.tsx';
import useConfirmLeave from '../../../hooks/useConfirmLeave.ts';
import {unsavedWork} from '../../../lib/unsavedWork.ts';

interface TopBarProps {
    // The title area: the project or group name, with its badges.
    name?: ReactNode;
    // Buttons on the right, before the bell and the account avatar.
    actions?: ReactNode;
    // Where the back arrow goes.
    to?: To;
    backLabel?: string;
}

/*
 * The top bar of a project or group: back, the logo mark, the name, then
 * the actions, the bell and the account avatar. The app header's four
 * destinations are one step back through the arrow.
 */
const TopBar: React.FC<TopBarProps> = ({
    name,
    actions,
    to,
    backLabel = 'Back'
}) => {
    const {storageUri} = useClientConfig();
    const {authData, isAuthenticated} = useAuthStateManager();
    const navigate = useNavigate();
    const confirmLeave = useConfirmLeave();

    // Leaving the page throws away unsaved editor work, so ask first.
    const leaveTo = async (target: To) => {
        if (await confirmLeave()) {
            navigate(target);
        }
    };
    const guardLink = (target: To) => (event: React.MouseEvent) => {
        if (unsavedWork()) {
            event.preventDefault();
            leaveTo(target);
        }
    };

    return (
        <header className="mx-project-header">
            <button
                type="button"
                className="btn btn-tertiary btn-icon-only"
                aria-label={backLabel}
                title={backLabel}
                onClick={() => leaveTo(to ?? '/')}
            >
                <Icon icon={ArrowLeftIcon} size="s" />
            </button>
            <Link
                to="/"
                className="mx-project-logo"
                aria-label="Mixiply home"
                onClick={guardLink('/')}
            >
                <img src="/images/logos/logo-mark.svg" alt="" />
            </Link>
            <span className="mx-project-divider" />
            <div className="mx-project-name">{name}</div>
            <div className="mx-project-actions">
                {actions}
                {isAuthenticated && (
                    <>
                        <span className="mx-project-divider" />
                        <MxNotifications />
                        <Link
                            to="/profile"
                            onClick={guardLink('/profile')}
                            className="mx-project-account"
                            aria-label="Settings"
                            title="Settings"
                        >
                            <Avatar
                                creator={{
                                    nickname: authData.nickname ?? '',
                                    name: authData.name,
                                    thumbnail: authData.id
                                        ? joinPath(
                                              storageUri,
                                              'thumbnails',
                                              `${authData.id}.png`
                                          )
                                        : null
                                }}
                                size="m"
                            />
                        </Link>
                    </>
                )}
            </div>
        </header>
    );
};

export default TopBar;
